import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import {
  AdOrderEnum,
  ADS_PER_PAGE,
  SORT_OPTIONS,
  DEFAULT_SCROLL_STEP_PX,
} from '@/components/Shop/Shop.constants';
import * as S from '@/components/Shop/ShopAds/ShopAdsSection.styles';
import * as ShopStyles from '@/components/Shop/Shop.styles';
import { GET_SHOP_ADS } from '@/api/queries/shop.queries';
import {
  ShopAdsResponse,
  ShopAdsSectionProps,
  ShopCategory,
  AdNode,
} from '@/api/types/shop.types';
import {
  buildCategoryAdsCountMap,
  shouldShowCategory,
  calculateCategoryCount,
} from '@/components/Shop/ShopView.helpers';
import { AdCardNew } from '@/components/AdCardNew';
import { InfiniteLoader } from '@/components/InfiniteLoader/InfiniteLoader';
import { SortBottomSheet } from '@/components/Shop/SortPopup';
import { ShopAdsSkeleton } from '@/components/Shop/ShopSkeleton/ShopAdsSkeleton';
import NoSsr from '@/components/NoSsr/NoSsr';
import useScreenSize from '@/hooks/useScreenSize';
import { debounce } from '@/utils/debounce';
import { useToastStore } from '@/store/useToastStore';
import { Toast } from '@/components/UI/Toast';
import {
  CollectionMasterModal,
  CollectionMasterSheet,
} from '@/features/BookmarkCollections';
import { Ad } from '@/api/types/common.types';
import { QUERY } from '@/constants/common';

export const ShopAdsSection: React.FC<ShopAdsSectionProps> = ({
  userLegacyId,
  categories,
}) => {
  const router = useRouter();
  const searchKeywords =
    (router.query[`${QUERY.q}[${QUERY.keywords}]`] as string) || '';
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    ''
  );
  const [selectedSort, setSelectedSort] = useState<AdOrderEnum>(
    AdOrderEnum.DateDesc
  );
  const { t } = useTranslation();
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isSortBottomSheetOpen, setIsSortBottomSheetOpen] = useState(false);
  const categoriesWrapperRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const { isLarge } = useScreenSize();

  const { data, loading, fetchMore } = useQuery<ShopAdsResponse>(GET_SHOP_ADS, {
    variables: {
      userLegacyId,
      categoryId: selectedCategoryId || undefined,
      orderType:
        selectedSort !== AdOrderEnum.DateDesc ? selectedSort : undefined,
      first: ADS_PER_PAGE,
      keywords: searchKeywords || undefined,
    },
    notifyOnNetworkStatusChange: true,
  });
  const checkCategoryScroll = useCallback(() => {
    const wrapper = categoriesWrapperRef.current;
    if (!wrapper) return;
    setCanScrollLeft(wrapper.scrollLeft > 0);
    setCanScrollRight(
      wrapper.scrollLeft + wrapper.clientWidth < wrapper.scrollWidth
    );
  }, []);

  const categoriesScrollStepRef = useRef<number>(
    DEFAULT_SCROLL_STEP_PX as number
  );

  useEffect(() => {
    const el = categoriesWrapperRef.current;
    if (!el) return;

    const computeStep = () => {
      const step = Math.min(
        DEFAULT_SCROLL_STEP_PX as number,
        Math.max(200, Math.round(el.clientWidth * 0.8))
      );
      categoriesScrollStepRef.current = step;
    };

    computeStep();

    const ro = new ResizeObserver(computeStep);
    ro.observe(el);

    window.addEventListener('resize', computeStep);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', computeStep);
    };
  }, [isLarge, categories.length]);

  const scrollCategories = useCallback(
    (direction: 'left' | 'right') => {
      const wrapper = categoriesWrapperRef.current;
      if (!wrapper) return;

      const step = categoriesScrollStepRef.current ?? DEFAULT_SCROLL_STEP_PX;
      wrapper.scrollBy({
        left: direction === 'left' ? -step : step,
        behavior: 'smooth',
      });

      setTimeout(() => {
        checkCategoryScroll();
      }, 220);
    },
    [checkCategoryScroll]
  );
  useEffect(() => {
    const wrapper = categoriesWrapperRef.current;
    if (!wrapper) return;
    checkCategoryScroll();

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        checkCategoryScroll();
        ticking = false;
      });
    };

    const onResize = debounce(checkCategoryScroll, 150);

    wrapper.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onResize);
    return () => {
      wrapper.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [data, checkCategoryScroll]);

  const onLoadMore = async () => {
    const pageInfo = data?.ShopAds?.pageInfo;
    if (!pageInfo?.hasNextPage || loading || !navigator.onLine) return;

    await fetchMore({
      variables: {
        after: pageInfo.endCursor,
        userLegacyId,
        categoryId: selectedCategoryId || undefined,
        first: ADS_PER_PAGE,
        keywords: searchKeywords || undefined,
        ...(selectedSort !== AdOrderEnum.DateDesc && {
          orderType: selectedSort,
        }),
      },
      updateQuery: (prevResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.ShopAds) return prevResult;
        return {
          ...fetchMoreResult,
          ShopAds: {
            ...fetchMoreResult.ShopAds,
            edges: [
              ...prevResult.ShopAds.edges,
              ...fetchMoreResult.ShopAds.edges.filter(
                newEdge =>
                  !prevResult.ShopAds.edges.find(
                    oldEdge => oldEdge.node.id === newEdge.node.id
                  )
              ),
            ],
          },
        };
      },
    });
  };

  const currentSortKey = SORT_OPTIONS.find(
    option => option.value === selectedSort
  )?.tKey;
  const ads: AdNode[] = (data?.ShopAds?.edges ?? []).map(edge => edge.node);
  const currentTotalCount = data?.ShopAds?.totalCount ?? 0;
  const isSelectedCategoryWithResults =
    selectedCategoryId !== '' && currentTotalCount > 0;

  const visibleCategories = useMemo(() => {
    if (!searchKeywords.trim()) {
      return categories;
    }

    const categoryAdsCount = buildCategoryAdsCountMap(ads);

    return categories
      .filter(cat => shouldShowCategory(cat, categoryAdsCount, categories))
      .map(cat => ({
        ...cat,
        count:
          cat.id === selectedCategoryId && isSelectedCategoryWithResults
            ? currentTotalCount
            : calculateCategoryCount(cat, categoryAdsCount, categories),
      }));
  }, [
    categories,
    ads,
    searchKeywords,
    isSelectedCategoryWithResults,
    currentTotalCount,
    selectedCategoryId,
  ]);

  useEffect(() => {
    if (searchKeywords.trim() && visibleCategories.length > 0) {
      const isCurrentCategoryVisible = visibleCategories.some(
        cat => cat.id === selectedCategoryId
      );
      if (!isCurrentCategoryVisible || selectedCategoryId === '') {
        const firstCategory = visibleCategories.find(cat => cat.id !== '');
        if (firstCategory) {
          setSelectedCategoryId(firstCategory.id);
        }
      }
    } else if (!searchKeywords.trim() && selectedCategoryId !== '') {
      setSelectedCategoryId('');
    }
  }, [searchKeywords, visibleCategories, selectedCategoryId]);

  const { openToast, closeToast, onAnimationComplete } = useToastStore();

  const [isCollectionMasterOpen, setIsCollectionMasterOpen] = useState(false);

  const handleAddToFavorites = useCallback((targetAd: Ad) => {
    closeToast();
    onAnimationComplete();

    setTimeout(() => {
      openToast({
        image: targetAd?.photo?.url,
        text: t('bookmark_collections.toast_tip'),
        action: t('bookmark_collections.toast_action'),
        onActionClick: handleToastActionClick,
      });
    }, 50);
  }, []);

  const handleRemoveFromFavorites = useCallback((targetAd: Ad) => {
    closeToast();
    onAnimationComplete();

    setTimeout(() => {
      openToast({
        image: targetAd?.photo?.url,
        text: t('bookmark_collections.unbookmarked'),
      });
    }, 50);
  }, []);

  const handleToastActionClick = useCallback(() => {
    closeToast();
    setIsCollectionMasterOpen(true);
  }, []);

  const handleCategoryClick = (
    isAll: boolean,
    isSearchActive: boolean,
    cat: ShopCategory
  ) => {
    if (isAll && isSearchActive) {
      const query = { ...router.query };
      delete query[`${QUERY.q}[${QUERY.keywords}]`];
      router.push(
        {
          pathname: router.pathname,
          query,
        },
        undefined,
        { shallow: true }
      );
    } else {
      setSelectedCategoryId(cat.id);
    }
  };

  return (
    <>
      {!data ? (
        <ShopAdsSkeleton />
      ) : (
        <NoSsr>
          <S.CategoriesSortWrapper>
            {isLarge ? (
              <>
                <S.CategoriesScrollContainer>
                  <S.CategoriesWrapper ref={categoriesWrapperRef}>
                    {visibleCategories.map(cat => {
                      const params = new URLSearchParams();
                      params.append('q[user_id]', userLegacyId);

                      const link = `/${cat.path}?${params.toString()}`;
                      const isAll = cat.id === '';
                      const isSearchActive = !!searchKeywords.trim();

                      return isLarge ? (
                        isAll ? (
                          <S.CategoryTag
                            key="all"
                            onClick={() =>
                              handleCategoryClick(isAll, isSearchActive, cat)
                            }
                            $isActive={selectedCategoryId === ''}
                          >
                            {cat.name} {cat.count}
                          </S.CategoryTag>
                        ) : isSearchActive ? (
                          <S.CategoryTag
                            key={cat.id}
                            onClick={() =>
                              handleCategoryClick(isAll, isSearchActive, cat)
                            }
                            $isActive={selectedCategoryId === cat.id}
                          >
                            {cat.name} {cat.count}
                          </S.CategoryTag>
                        ) : (
                          <S.CategoryTagLink key={cat.id} href={link}>
                            {cat.name} {cat.count}
                          </S.CategoryTagLink>
                        )
                      ) : (
                        <S.CategoryTag
                          key={cat.id || 'all'}
                          onClick={() =>
                            handleCategoryClick(isAll, isSearchActive, cat)
                          }
                          $isActive={
                            selectedCategoryId === cat.id ||
                            (!selectedCategoryId && cat.id === '')
                          }
                        >
                          {cat.name} {cat.count}
                        </S.CategoryTag>
                      );
                    })}
                  </S.CategoriesWrapper>
                  {canScrollLeft && (
                    <S.CategoriesScrollButton
                      $side="left"
                      onClick={() => scrollCategories('left')}
                    />
                  )}
                  {canScrollRight && (
                    <S.CategoriesScrollButton
                      $side="right"
                      onClick={() => scrollCategories('right')}
                    />
                  )}
                </S.CategoriesScrollContainer>

                <S.SortWrapper>
                  <S.SortDropdown
                    $isSortOpen={isSortOpen}
                    onClick={() => setIsSortOpen(prev => !prev)}
                  >
                    {currentSortKey ? t(currentSortKey) : ''}
                  </S.SortDropdown>
                  {isSortOpen && (
                    <S.SortPopup>
                      <S.SortPopupContent>
                        {SORT_OPTIONS.map(option => (
                          <S.SortLabel
                            key={option.value}
                            onClick={() => {
                              setSelectedSort(option.value);
                              setIsSortOpen(prev => !prev);
                            }}
                          >
                            {t(option.tKey)}
                            <S.SortRadioWrapper
                              key={option.value}
                              onClick={() => setSelectedSort(option.value)}
                            >
                              <S.SortRadio
                                $active={selectedSort === option.value}
                              />
                            </S.SortRadioWrapper>
                          </S.SortLabel>
                        ))}
                      </S.SortPopupContent>
                    </S.SortPopup>
                  )}
                </S.SortWrapper>
              </>
            ) : (
              <S.MobileSortAndCategories>
                <S.CategoriesScrollContainer>
                  <S.CategoriesWrapper ref={categoriesWrapperRef}>
                    <S.SortMobileButton
                      onClick={() => setIsSortBottomSheetOpen(true)}
                    />
                    {categories.map(cat => (
                      <S.CategoryTag
                        key={cat.id}
                        onClick={() => setSelectedCategoryId(cat.id)}
                        $isActive={selectedCategoryId === cat.id}
                      >
                        {cat.name} {cat.count}
                      </S.CategoryTag>
                    ))}
                  </S.CategoriesWrapper>
                </S.CategoriesScrollContainer>
              </S.MobileSortAndCategories>
            )}
          </S.CategoriesSortWrapper>

          {ads.length === 0 ? (
            <ShopStyles.ShopAdsEmpty>
              {t('shop.ads_empty')}
            </ShopStyles.ShopAdsEmpty>
          ) : (
            <S.AdsGrid>
              {ads.map((ad, idx) => (
                <AdCardNew
                  key={`${ad.id}_${idx}`}
                  ad={ad}
                  onAddBookmark={handleAddToFavorites}
                  onRemoveBookmark={handleRemoveFromFavorites}
                  priority={!idx}
                />
              ))}
            </S.AdsGrid>
          )}
          {!isLarge && (
            <SortBottomSheet
              isOpen={isSortBottomSheetOpen}
              close={() => setIsSortBottomSheetOpen(false)}
              selectedSort={selectedSort}
              onSelect={setSelectedSort}
              text={t('shop.sort')}
            />
          )}

          {data?.ShopAds?.pageInfo?.hasNextPage && (
            <InfiniteLoader onVisit={onLoadMore} />
          )}
          {isLarge ? (
            <CollectionMasterModal
              isOpen={isCollectionMasterOpen}
              onClose={() => setIsCollectionMasterOpen(false)}
            />
          ) : (
            <CollectionMasterSheet
              isOpen={isCollectionMasterOpen}
              onClose={() => setIsCollectionMasterOpen(false)}
            />
          )}

          <Toast />
        </NoSsr>
      )}
    </>
  );
};
